import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageSquare, Send, X, Bot, User, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AIAgentWidget({ siteId, siteName, onContentChanged }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `master-agent-${siteId}-${Date.now()}`);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const token = localStorage.getItem('masterToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        text: `Hi! I'm your AI editing assistant for **${siteName}**. Tell me what you'd like to change — for example:\n\n- "Change the hero title to Welcome to Our Store"\n- "Hide the gallery section"\n- "Update the about text to be more luxurious"\n- "Move the signup section above products"`,
        actions: []
      }]);
    }
  }, [open, siteName]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/master/ai-agent`, {
        message: text,
        site_id: siteId,
        session_id: sessionId
      }, { headers });

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: res.data.response,
        actions: res.data.actions_applied || []
      }]);

      if (res.data.actions_applied?.length > 0 && onContentChanged) {
        onContentChanged();
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Sorry, I encountered an error. Please try again.',
        actions: []
      }]);
      toast.error('AI agent error');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (text) => {
    // Simple markdown-like formatting
    return text
      .replace(/```json[\s\S]*?```/g, (match) => {
        const code = match.replace(/```json\n?/, '').replace(/```/, '');
        return `<div class="bg-black/30 rounded-lg p-2 mt-2 mb-2 text-xs font-mono overflow-x-auto text-[#00CC99]">${code.replace(/</g, '&lt;')}</div>`;
      })
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white shadow-[0_0_30px_rgba(139,92,246,0.5)] hover:shadow-[0_0_40px_rgba(139,92,246,0.7)] transition-all flex items-center justify-center group"
          data-testid="ai-agent-trigger"
        >
          <Sparkles size={22} className="group-hover:scale-110 transition-transform" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[420px] h-[600px] bg-[#0E0E23]/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden" data-testid="ai-agent-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 bg-gradient-to-r from-[#8B5CF6]/10 to-[#EC4899]/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] flex items-center justify-center">
                <Bot size={18} className="text-white" />
              </div>
              <div>
                <h3 className="text-white text-sm font-semibold">AI Editor</h3>
                <p className="text-[10px] text-[#9CA3AF]">{siteName}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => { setMessages([]); }} className="p-2 text-[#64748B] hover:text-white rounded-lg transition-colors" title="Clear" data-testid="ai-agent-clear">
                <RefreshCw size={14} />
              </button>
              <button onClick={() => setOpen(false)} className="p-2 text-[#64748B] hover:text-white rounded-lg transition-colors" data-testid="ai-agent-close">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="ai-agent-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] flex items-center justify-center shrink-0 mt-0.5">
                    <Bot size={14} className="text-white" />
                  </div>
                )}
                <div className={`max-w-[85%] ${msg.role === 'user'
                  ? 'bg-[#8B5CF6]/20 border border-[#8B5CF6]/30 rounded-2xl rounded-br-md'
                  : 'bg-white/5 border border-white/5 rounded-2xl rounded-bl-md'
                } px-3.5 py-2.5`}>
                  <div className="text-sm text-[#E2E8F0] leading-relaxed" dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }} />
                  {msg.actions?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/5">
                      {msg.actions.map((a, j) => (
                        <p key={j} className="text-[10px] text-[#00CC99] flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00CC99]" /> {a}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <User size={14} className="text-white" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2.5 justify-start">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] flex items-center justify-center shrink-0">
                  <Bot size={14} className="text-white" />
                </div>
                <div className="bg-white/5 border border-white/5 rounded-2xl rounded-bl-md px-4 py-3">
                  <Loader2 size={16} className="animate-spin text-[#8B5CF6]" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-white/5 bg-[#0A0A1F]">
            <div className="flex items-center gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type an edit command..."
                rows={1}
                className="flex-1 bg-white/5 border border-white/10 text-white text-sm px-3.5 py-2.5 rounded-xl resize-none placeholder:text-[#64748B] focus:border-[#8B5CF6]/50 focus:outline-none transition-colors"
                data-testid="ai-agent-input"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white flex items-center justify-center disabled:opacity-40 hover:shadow-[0_0_15px_rgba(139,92,246,0.4)] transition-all shrink-0"
                data-testid="ai-agent-send"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
